import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { EmissionCreditService } from "../../services/emission-credit.service";
import { ProjectService } from "src/app/pages/projects-repository/services/project.service";
import { B3SelectItem } from 'src/app/shared/models/b3-select-item.model';
import { PsaHarvest, PsaProjectEmission } from "src/app/shared/interfaces/psa-project-emission";
import { ProjectTable } from "src/app/components/project-table/project-table.component";
import { PsaProjectData, PsaProjectFilter } from "src/app/shared/interfaces/psa-project-register";
import { ValidateTwoValueInString } from 'src/app/helpers/Validators/specific-value-in-string-validator';
import { catchError, firstValueFrom, map, Observable, of } from "rxjs";
import { Router } from "@angular/router";
import { CpfCnpjValidator } from 'src/app/helpers/Validators/CpfCnpjValidator/cpf-cnpj.validator';
import { detectDocumentType, formatCPFOrCNPJ } from 'src/app/helpers/utils/cpf-cnpj.util';
import { formatRegisterAccountCode } from 'src/app/helpers/utils/account.util';
import { MenuService } from 'src/app/components/menu/service/menu.service';
import {
  buildEmissionRegisterErrorViewModel,
  buildEmissionRegisterSuccessViewModel,
  EmissionRegisterErrorViewModel,
  EmissionRegisterSuccessViewModel,
} from './modals/emission-register-modal.model';
import {
  harvestYearWithinPeriodValidator,
  isHarvestYearWithinPeriod,
} from 'src/app/helpers/Validators/harvest-year-period.validator';
import { toDateOnly } from 'src/app/helpers/Validators/emission-date.validator';

@Component({
  standalone: false,
  selector: 'app-emission-register',
  templateUrl: './emission-register.component.html',
  styleUrls: ['./emission-register.component.scss']
})
export class EmissionRegisterComponent extends ProjectTable<PsaHarvest> implements OnInit {

  override onPageChange(currentPage: number): void {
  }

  public formRegister: FormGroup = new FormGroup({});

  characterCount: number = 0;
  maskCpfOrCnpj: string = '';

  public today = new Date();
  public minDate = new Date(1900, 0, 1);

  public startDate = new Date(1900, 0, 1);
  public isInvalidStartDate: boolean = false;

  public endDate = new Date();
  public isInvalidEndDate: boolean = false;

  public isButtonDisabled: boolean = false;
  public harvestValidationAttempted = false;
  public formValidationAttempted = false;
  public invalidTableHarvestItems = false;

  showSuccessModal = false;
  successViewModel: EmissionRegisterSuccessViewModel | null = null;
  showErrorModal = false;
  errorViewModel: EmissionRegisterErrorViewModel | null = null;

  private readonly harvestFieldNames = ['harvestYear', 'harvestQuantity', 'developer'] as const;
  private readonly periodFieldNames = ['startDate', 'endDate'] as const;

  public selectItens: B3SelectItem[] = [];
  private listPsaProjectData: PsaProjectData[] = [];

  public developerSelectItens: B3SelectItem[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private emissionRegisterService: EmissionCreditService,
    private projectService: ProjectService,
    private router: Router,
    private menuService: MenuService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.initFormRegister();

    this.formRegister.get('details')?.valueChanges.subscribe(value => {
      this.syncDetailsCharacterCount(value);
    });

    this.formRegister.get('cpfCnpjCode')?.valueChanges.subscribe(() => {
      this.maskCpfOrCnpj = '';
    });

    this.periodFieldNames.forEach((fieldName) => {
      this.formRegister.get(fieldName)?.valueChanges.subscribe(() => {
        this.syncPeriodDateFromForm(fieldName);
        this.formRegister.get('harvestYear')?.updateValueAndValidity({ emitEvent: false });
        this.validateIncludedHarvestItemsAgainstPeriod();
      });
    });

    this.formRegister.get('emissionNumber')?.disable({ emitEvent: false });
    this.formRegister.get('totalQuantity')?.disable({ emitEvent: false });
    this.formRegister.get('cpfCnpjCode')?.disable({ emitEvent: false });

    this.loadProjects();
    this.validateObjetSessionAndLoad();
    this.menuService.whenMenuLoaded$().subscribe(() => this.loadHolderAccount());
    this.loadHolderAccount();
  }

  get developerDocumentMask(): 'cpf' | 'cnpj' | 'text' {
    const docType = detectDocumentType(this.formRegister.get('cpfCnpjCode')?.value || '');
    if (docType === 'CNPJ') {
      return 'cnpj';
    }
    if (docType === 'CPF') {
      return 'cpf';
    }
    return 'text';
  }

  get minDateIso(): string {
    return this.toIsoDate(this.minDate);
  }

  get maxDateIso(): string {
    return this.toIsoDate(this.today);
  }

  get maxStartDateIso(): string {
    return this.toIsoDate(this.endDate && this.endDate <= this.today ? this.endDate : this.today);
  }

  get minEndDateIso(): string {
    return this.toIsoDate(this.startDate ?? this.minDate);
  }

  private validateObjetSessionAndLoad() {
    const emissionCloneObject = sessionStorage.getItem("CLONE_EMISSION_OBJECT");

    if (emissionCloneObject) {
      const emissionObject: PsaProjectEmission = JSON.parse(emissionCloneObject) as PsaProjectEmission;
      this.formRegister.get("rmLink")?.setValue(emissionObject.rmLink);
      this.formRegister.get("details")?.setValue(emissionObject.details);

      this.formRegister.get("startDate")?.setValue(this.toIsoDate(this.dateParser(emissionObject.startDate)));
      this.startDate = this.dateParser(emissionObject.startDate);

      this.formRegister.get("endDate")?.setValue(this.toIsoDate(this.dateParser(emissionObject.endDate)));
      this.endDate = this.dateParser(emissionObject.endDate);

      this.itens = emissionObject.harvests
      this.countHarvestQuantity();
    }

    sessionStorage.removeItem("CLONE_EMISSION_OBJECT");

  }

  private dateParser(date: string) {

    if (date) {
      const partes = date.split('/');
      const dia = parseInt(partes[0], 10);
      const mes = parseInt(partes[1], 10) - 1;
      const ano = parseInt(partes[2], 10);
      return new Date(ano, mes, dia);
    }

    return new Date();
  }


  public initFormRegister(): void {
    this.formRegister = this.formBuilder.group({
      startDate: ['', [Validators.required]],
      endDate: ['', [Validators.required]],
      rmLink: ['', [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(/^(https?:\/\/)?[\w.-]+\.[a-zA-Z]{2,63}([\/\w\-~!$&'()*+,;=:@%#?]+)*$/)
      ]],
      details: ['', [Validators.required, Validators.maxLength(500)]],
      project: ['', Validators.required],
      totalQuantity: [''],
      emissionNumber: [''],
      projectId: [],
      harvestYear: ['', [harvestYearWithinPeriodValidator()]],
      harvestQuantity: [''],
      developer: [''],
      cpfCnpjCode: ['', [Validators.required, CpfCnpjValidator.validate]],
      holderCode: ['', [Validators.required, Validators.maxLength(10), Validators.minLength(10), ValidateTwoValueInString('00', '10', 6, 7)]],
    })
  }

  async register(): Promise<void> {
    this.formValidationAttempted = true;
    this.markFieldsAsTouched([
      'holderCode',
      'cpfCnpjCode',
      'project',
      'startDate',
      'endDate',
      'rmLink',
      'details',
    ]);

    if (this.formRegister.invalid || !this.itens.length || this.characterCount > 500) {
      return;
    }

    if (this.hasInvalidHarvestItemsForPeriod()) {
      this.invalidTableHarvestItems = true;
      this.harvestValidationAttempted = true;
      this.markFieldsAsTouched([...this.periodFieldNames, 'harvestYear']);
      this.formRegister.get('harvestYear')?.updateValueAndValidity({ emitEvent: false });
      return;
    }

    this.isButtonDisabled = true;

    try {
      let emissionObject = this.parseFormToObject();
      let res = await firstValueFrom(this.emissionRegisterService.projectEmissionRegister(emissionObject));
      this.treatCreditRecordSuccess(res);
      this.cleanForm();
    } catch (error) {
      console.error("Erro ao gravar Emissão de Crédito - stackError: ", error);
      this.treatCreditRecordError(error);
    }

    this.isButtonDisabled = false;
  }

  private loadProjects() {
    this.getAllConfirmedProjects().subscribe(projects => {
      if (projects) {

        this.listPsaProjectData = projects;

        this.selectItens = projects.map(projects => ({
          inputId: projects.projectId ? projects.projectId : 0,
          label: projects.projectName ? projects.projectId + '-' + projects.projectName : "-"
        }));
      } else {
        console.error("Não foram encontrados projetos aprovados para o usuario logado")
      }
    });
  }

  private getAllConfirmedProjects(): Observable<PsaProjectData[] | undefined> {
    const filter: PsaProjectFilter = {
      status: 'PROJECT_REGISTERED_IN_B3',
    };

    return this.projectService.getAllProjects(filter, 200).pipe(
      map(projects => projects.length ? projects : undefined),
      catchError(error => {
        console.error("Erro ao carregar os projetos. Stack error: ", error);
        return of(undefined);
      })
    );
  }

  loadDevelopers(): void {
    const selectProject = this.formRegister.get("project")?.value;
    const projectId = selectProject?.inputId ?? selectProject;

    if (projectId == null || projectId === '') {
      this.formRegister.get('cpfCnpjCode')?.setValue('', { emitEvent: false });
      return;
    }

    const project = this.listPsaProjectData.find((proj: PsaProjectData) => proj.projectId === projectId);

    if (project?.developers?.length) {
      this.developerSelectItens = project.developers.map(developer => ({
        inputId: developer.id ? developer.id : 0,
        label: developer.developerName ? developer.developerName : "-"
      }));

      const principalDeveloper = project.developers.find(developer => developer.principal) ?? project.developers[0];
      const cpfCnpjControl = this.formRegister.get('cpfCnpjCode');
      cpfCnpjControl?.setValue(
        principalDeveloper?.cpfCnpj ? formatCPFOrCNPJ(principalDeveloper.cpfCnpj) : '',
        { emitEvent: false },
      );

      this.setEmissionNumber(project);
      this.setProjectId(project);
    } else {
      console.error("Não foi encontrado nenhum Desenvolvedor associado a este projeto");
    }
  }

  onProjectChange(): void {
    this.loadDevelopers();
  }

  private setEmissionNumber(project: PsaProjectData) {
    if (project.projectId) {
      this.emissionRegisterService.getNextEmissionNumber(project.projectId).subscribe(
        (value: any) => {
          this.formRegister.get("emissionNumber")?.setValue(value.data.newIssuanceCode);
        }
      )
    } else {
      console.error("Não foi possivel obter o proximo numero de emissão - Id do projeto não encontrado")
    }
  }

  setProjectId(project: PsaProjectData) {
    this.formRegister.get("projectId")?.setValue(project.projectId);
  }

  public cleanForm() {
    this.formRegister.reset();
    this.formRegister.get('cpfCnpjCode')?.disable({ emitEvent: false });
    this.formRegister.get('holderCode')?.enable({ emitEvent: false });
    this.maskCpfOrCnpj = '';
    this.startDate = this.minDate;
    this.isInvalidStartDate = false;
    this.endDate = new Date();
    this.isInvalidEndDate = false;
    this.harvestValidationAttempted = false;
    this.formValidationAttempted = false;
    this.invalidTableHarvestItems = false;
    this.characterCount = 0;
    this.itens = [];
    this.developerSelectItens = [];
    this.formRegister.get('emissionNumber')?.disable({ emitEvent: false });
    this.formRegister.get('totalQuantity')?.disable({ emitEvent: false });
    this.loadHolderAccount();
  }

  private loadHolderAccount(): void {
    const rawAccount = sessionStorage.getItem('USER_HOLDER_ACCOUNT');
    if (!rawAccount) {
      return;
    }

    const holderControl = this.formRegister.get('holderCode');
    if (!holderControl) {
      return;
    }

    holderControl.enable({ emitEvent: false });
    holderControl.setValue(formatRegisterAccountCode(rawAccount), { emitEvent: false });
    holderControl.disable({ emitEvent: false });
  }

  public onChangesInputCpfOrCnpj(): void {
    this.maskCpfOrCnpj = '';
    const value = this.formRegister.get('cpfCnpjCode')?.value;
    this.formRegister.get('cpfCnpjCode')?.setValue(value?.replace(/[./-]/g, '').trim(), { emitEvent: false });
    const docType = detectDocumentType(value || '');

    if (docType === 'CPF') {
      this.maskCpfOrCnpj = '000.000.000-00';
    } else if (docType === 'CNPJ') {
      this.maskCpfOrCnpj = 'AA.AAA.AAA/AAAA-AA';
    } else {
      this.maskCpfOrCnpj = '';
    }
  }

  public shouldShowFieldError(fieldName: string): boolean {
    const field = this.formRegister.get(fieldName);
    if (field == null) {
      return false;
    }

    const isHarvestField = this.harvestFieldNames.includes(
      fieldName as typeof this.harvestFieldNames[number],
    );

    // Campos de safra só exibem erro após tentativa de incluir (evita combo vermelho após limpar).
    if (isHarvestField) {
      return this.harvestValidationAttempted;
    }

    if (field.touched) {
      return true;
    }

    if (this.formValidationAttempted) {
      return true;
    }

    if (this.harvestValidationAttempted) {
      return this.periodFieldNames.includes(fieldName as typeof this.periodFieldNames[number]);
    }

    return false;
  }

  public checkHasError(fieldName: string, errorCode?: string): boolean {
    const field = this.formRegister.get(fieldName);
    if (field == null || field.disabled || !this.shouldShowFieldError(fieldName)) {
      return false;
    }

    if (this.isHarvestFieldEmpty(fieldName, field.value)) {
      return errorCode == null || errorCode === 'required';
    }

    if (errorCode == null) {
      return field.invalid;
    }

    return field.hasError(errorCode);
  }

  checkDateFieldError(fieldName: string, errorCode: string): boolean {
    const field = this.formRegister.get(fieldName);
    if (field == null || !this.shouldShowFieldError(fieldName)) {
      return false;
    }

    if (this.isPeriodFieldEmpty(field.value)) {
      return errorCode === 'required';
    }

    return field.hasError(errorCode);
  }

  checkHarvestYearPeriodError(errorCode: 'harvestBeforePeriod' | 'harvestAfterPeriod'): boolean {
    const field = this.formRegister.get('harvestYear');
    if (field == null || !this.shouldShowFieldError('harvestYear')) {
      return false;
    }

    if (this.isHarvestFieldEmpty('harvestYear', field.value)) {
      return false;
    }

    return field.hasError(errorCode);
  }

  showHarvestYearPeriodError(): boolean {
    if (!this.shouldShowFieldError('harvestYear')) {
      return false;
    }

    const field = this.formRegister.get('harvestYear');
    if (field == null || this.isHarvestFieldEmpty('harvestYear', field.value)) {
      return false;
    }

    return field.hasError('harvestBeforePeriod') || field.hasError('harvestAfterPeriod');
  }

  showDateRangeError(fieldName: 'startDate' | 'endDate'): boolean {
    if (!this.shouldShowFieldError(fieldName)) {
      return false;
    }

    return fieldName === 'startDate' ? this.isInvalidStartDate : this.isInvalidEndDate;
  }

  onStartDateChange(event: Event): void {
    const date = this.resolveDateValue((event as CustomEvent<{ value?: string | Date }>).detail?.value);
    if (!date) {
      return;
    }
    this.startDate = date;
    this.isInvalidStartDate = date > this.today || date > this.endDate;
    this.formRegister.get('harvestYear')?.updateValueAndValidity({ emitEvent: false });
    this.validateIncludedHarvestItemsAgainstPeriod();
  }

  onEndDateChange(event: Event): void {
    const date = this.resolveDateValue((event as CustomEvent<{ value?: string | Date }>).detail?.value);
    if (!date) {
      return;
    }
    this.endDate = date;
    this.isInvalidEndDate = date > this.today || date < this.startDate;
    this.formRegister.get('harvestYear')?.updateValueAndValidity({ emitEvent: false });
    this.validateIncludedHarvestItemsAgainstPeriod();
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private fromIsoDate(iso: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(year, month - 1, day);

    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }

  public updateCharacterCount(event: Event): void {
    const input = event.target as HTMLTextAreaElement;
    this.syncDetailsCharacterCount(input.value);
  }

  public addEmissionVintage() {
    if (this.isPeriodIncomplete()) {
      this.failHarvestValidation();
      return;
    }

    this.formRegister.get('harvestYear')?.updateValueAndValidity({ emitEvent: false });

    if (this.hasHarvestYearPeriodValidationError()) {
      this.failHarvestValidation();
      return;
    }

    const harvestYear = this.formRegister.get("harvestYear");
    const harvestQuantity = this.formRegister.get("harvestQuantity");
    const harvestDeveloper = this.formRegister.get("developer");

    const yearValue = Number(harvestYear?.value);
    const quantityValue = Number(harvestQuantity?.value);

    if (
      Number.isFinite(yearValue) && yearValue > 0 &&
      Number.isFinite(quantityValue) && quantityValue > 0 &&
      this.resolveDeveloperSelection(harvestDeveloper?.value)
    ) {
      const developer = this.resolveDeveloperSelection(harvestDeveloper?.value)!;
      const psaHarvest: PsaHarvest = {} as PsaHarvest;
      psaHarvest.harvestYear = yearValue;
      psaHarvest.quantity = quantityValue;
      psaHarvest.developerName = developer.label;
      psaHarvest.developerId = Number(developer.inputId);

      this.itens = [...this.itens, psaHarvest];
      this.invalidTableHarvestItems = false;
      this.countHarvestQuantity();
      this.resetHarvestInputFields();
      return;
    }

    this.failHarvestValidation();
  }

  private failHarvestValidation(): void {
    this.harvestValidationAttempted = true;
    this.markFieldsAsTouched([...this.harvestFieldNames, ...this.periodFieldNames]);
  }

  private resetHarvestInputFields(): void {
    this.harvestValidationAttempted = false;

    this.harvestFieldNames.forEach((fieldName) => {
      const control = this.formRegister.get(fieldName);
      control?.setValue(fieldName === 'developer' ? null : '', { emitEvent: false });
      control?.markAsPristine();
      control?.markAsUntouched();
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private syncDetailsCharacterCount(value: unknown): void {
    const text = typeof value === 'string' ? value : '';
    const detailsControl = this.formRegister.get('details');

    if (text.length > 500) {
      const truncated = text.slice(0, 500);
      detailsControl?.setValue(truncated, { emitEvent: false });
      detailsControl?.updateValueAndValidity({ emitEvent: false });
      this.characterCount = 500;
      return;
    }

    this.characterCount = text.length;
  }

  private isPeriodIncomplete(): boolean {
    return this.isPeriodFieldEmpty(this.formRegister.get('startDate')?.value)
      || this.isPeriodFieldEmpty(this.formRegister.get('endDate')?.value)
      || this.isInvalidStartDate
      || this.isInvalidEndDate;
  }

  private hasHarvestYearPeriodValidationError(): boolean {
    const harvestYearControl = this.formRegister.get('harvestYear');
    if (!harvestYearControl || this.isHarvestFieldEmpty('harvestYear', harvestYearControl.value)) {
      return false;
    }

    return harvestYearControl.hasError('harvestBeforePeriod')
      || harvestYearControl.hasError('harvestAfterPeriod');
  }

  private validateIncludedHarvestItemsAgainstPeriod(): void {
    if (!this.itens.length) {
      this.invalidTableHarvestItems = false;
      return;
    }

    this.invalidTableHarvestItems = this.hasInvalidHarvestItemsForPeriod();
    if (this.invalidTableHarvestItems) {
      this.harvestValidationAttempted = true;
    }
  }

  private syncPeriodDateFromForm(fieldName: 'startDate' | 'endDate'): void {
    const date = toDateOnly(this.formRegister.get(fieldName)?.value);
    if (!date) {
      return;
    }

    if (fieldName === 'startDate') {
      this.startDate = date;
      this.isInvalidStartDate = date > this.today || date > this.endDate;
      return;
    }

    this.endDate = date;
    this.isInvalidEndDate = date > this.today || date < this.startDate;
  }

  private resolveDateValue(value: unknown): Date | null {
    return toDateOnly(value);
  }

  private hasInvalidHarvestItemsForPeriod(): boolean {
    const startDate = this.formRegister.get('startDate')?.value;
    const endDate = this.formRegister.get('endDate')?.value;

    return this.itens.some(item =>
      !isHarvestYearWithinPeriod(item.harvestYear, startDate, endDate),
    );
  }

  private markFieldsAsTouched(fieldNames: readonly string[]): void {
    fieldNames.forEach((fieldName) => this.formRegister.get(fieldName)?.markAsTouched());
  }

  private isHarvestFieldEmpty(fieldName: string, value: unknown): boolean {
    if (!this.harvestFieldNames.includes(fieldName as typeof this.harvestFieldNames[number])) {
      return false;
    }

    if (value == null || value === '') {
      return true;
    }

    if (fieldName === 'developer') {
      return !this.resolveDeveloperSelection(value);
    }

    const numericValue = Number(value);
    return !Number.isFinite(numericValue) || numericValue <= 0;
  }

  private isPeriodFieldEmpty(value: unknown): boolean {
    if (value == null || value === '') {
      return true;
    }

    return toDateOnly(value) == null;
  }

  public removeEmissionVintage(row: any) {
    this.itens = this.itens.filter(i => i !== row);
    this.countHarvestQuantity();
    this.validateIncludedHarvestItemsAgainstPeriod();
  }

  private countHarvestQuantity(): void {
    const total = this.itens.reduce((sum: number, item: PsaHarvest) => {
      return sum + Number(item.quantity ?? 0);
    }, 0);

    const control = this.formRegister.get('totalQuantity');
    control?.setValue(total > 0 ? String(total) : '', { emitEvent: false });
  }

  private parseFormToObject(): PsaProjectEmission {
    const raw = this.formRegister.getRawValue();

    let psaProjectEmission = {} as PsaProjectEmission;

    psaProjectEmission.projectId = raw.projectId;
    psaProjectEmission.emissionNumber = raw.emissionNumber;
    psaProjectEmission.emissionQuantity = Number(raw.totalQuantity) || 0;
    psaProjectEmission.startDate = this.fromIsoDate(raw.startDate) ?? raw.startDate;
    psaProjectEmission.endDate = this.fromIsoDate(raw.endDate) ?? raw.endDate;
    psaProjectEmission.rmLink = raw.rmLink;
    psaProjectEmission.details = raw.details;
    psaProjectEmission.cpfCnpjCode = raw.cpfCnpjCode;
    psaProjectEmission.holderCode = raw.holderCode;
    psaProjectEmission.harvests = this.itens;

    return psaProjectEmission;
  }

  treatCreditRecordSuccess(res: unknown): void {
    this.successViewModel = buildEmissionRegisterSuccessViewModel(res, this.getSelectedProjectLabel());
    this.showSuccessModal = true;
  }

  treatCreditRecordError(erro: unknown): void {
    console.error('Erro ao gravar Emissão de Crédito - stackError: ', erro);
    this.errorViewModel = buildEmissionRegisterErrorViewModel();
    this.showErrorModal = true;
  }

  onSuccessModalAction(action: 'new-emission' | 'consult' | 'dismiss'): void {
    this.showSuccessModal = false;
    this.successViewModel = null;

    if (action === 'new-emission') {
      this.cleanForm();
    }

    if (action === 'consult') {
      this.navigateToEmissionConsult();
    }
  }

  onErrorModalAction(_action: 'retry' | 'support' | 'dismiss'): void {
    this.showErrorModal = false;
    this.errorViewModel = null;
  }

  private getSelectedProjectLabel(): string {
    const project = this.formRegister.get('project')?.value;

    if (project && typeof project === 'object' && 'label' in project) {
      return String((project as B3SelectItem).label ?? '');
    }

    const projectId = this.formRegister.get('projectId')?.value;
    const selectedItem = this.selectItens.find(item => item.inputId == projectId);
    return selectedItem?.label ?? '-';
  }

  navigateToEmissionConsult() {
    this.router.navigate(['/consulta-emissao-credito']);
  }

  private resolveDeveloperSelection(value: unknown): B3SelectItem | null {
    if (value == null || value === '') {
      return null;
    }

    if (typeof value === 'object' && 'inputId' in (value as B3SelectItem)) {
      const selected = value as B3SelectItem;
      return selected.inputId != null ? selected : null;
    }

    const inputId = Number(value);
    if (!Number.isFinite(inputId)) {
      return null;
    }

    return this.developerSelectItens.find(item => item.inputId == inputId) ?? null;
  }

}